'use client'

import { useEffect } from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { addSortingQueryParamsToPath, extractQueryParamsForSorting } from '../../../MyStichtingOpenHelpers/src/static'
import { Sorting } from '../types/Sorting'

interface SortingDefaults {
    bindToURL: boolean
    initialSorting: Sorting
}

interface UseSortingQueryParamsInURLProps extends SortingDefaults {
    sorting: Sorting
}

/**
 * Reads the sorting state a sortable overview should start with. Falls back to the sorting it was
 * configured with when the state is not kept in the URL.
 */
export const useInitialSortingFromURL = ({ bindToURL, initialSorting }: SortingDefaults): Sorting => {
    const searchParams = useSearchParams()

    if (!bindToURL) {
        return initialSorting
    }

    return extractQueryParamsForSorting(searchParams, initialSorting)
}

/**
 * Keeps the browser URL in step with the sorted column and its direction so the order survives a
 * refresh or a shared link. The sorting an overview starts with is left out to keep the URL clean.
 */
export const useSortingQueryParamsInURL = ({
    bindToURL,
    initialSorting,
    sorting,
}: UseSortingQueryParamsInURLProps): void => {
    const searchParams = useSearchParams()
    const pathname = usePathname()
    const router = useRouter()

    useEffect(() => {
        if (!bindToURL) return
        const currentQuery = searchParams.toString()
        const currentUrl = currentQuery ? `${pathname}?${currentQuery}` : pathname
        const nextUrl = addSortingQueryParamsToPath(currentUrl, sorting, initialSorting)
        // Replacing changes searchParams, which re-runs this effect. Bailing out on an unchanged
        // url is what makes it settle instead of looping.
        if (nextUrl === currentUrl) return
        router.replace(nextUrl, { scroll: false })
    }, [bindToURL, sorting, pathname, searchParams])
}
